import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSceneTree, useSceneCard, useCreateGroup } from '@/api/use-scenes';
import { useTags } from '@/api/use-tags';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { CopyButton } from '@/components/shared/CopyButton';
import {
  ChevronDown,
  ChevronRight,
  Star,
  Map,
  FileText,
  Pencil,
  Info,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  Plus,
  Play,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { LOG_TYPE_COLORS } from '@/lib/constants';
import { useToast } from '@/hooks/use-toast';
import { useItems } from '@/api/use-items';
import type { SceneCard } from '@/api/types';

/* ---------- helpers ---------- */

function DifficultyStars({ difficulty }: { difficulty: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'w-4 h-4',
            i < difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30',
          )}
        />
      ))}
    </div>
  );
}

function displayScore(raw: number | null | undefined): string {
  if (raw == null) return '--';
  // avg_score from tree API is 0-1 scale, latest_score is 0-10
  // Display all scores on 0-10 scale
  const val = raw <= 1 ? raw * 10 : raw;
  return val.toFixed(1);
}

const TAG_COLORS: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-700',
  orange: 'bg-orange-100 text-orange-700',
  green: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700',
  purple: 'bg-purple-100 text-purple-700',
  yellow: 'bg-amber-100 text-amber-700',
};

function tagColorClass(color: string | null): string {
  return TAG_COLORS[color ?? ''] ?? 'bg-slate-100 text-slate-600';
}

/* ---------- main component ---------- */

export default function Scenes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tree, isLoading } = useSceneTree();
  const { data: tags } = useTags();
  const { toast } = useToast();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(new Set());
  const [selectedCardId, setSelectedCardId] = useState<string | null>(searchParams.get('card'));

  // Auto-select card from URL param and expand tree to reveal it
  useEffect(() => {
    const cardParam = searchParams.get('card');
    if (!cardParam || !tree) return;
    setSelectedCardId(cardParam);
    // Walk tree to find card and expand its group+scene
    for (const group of tree) {
      for (const scene of group.scenes) {
        if (scene.cards.some((c) => c.id === cardParam)) {
          setExpandedGroups((prev) => new Set(prev).add(group.id));
          setExpandedScenes((prev) => new Set(prev).add(scene.id));
          break;
        }
      }
    }
    // Clear URL param after consuming
    searchParams.delete('card');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const navigate = useNavigate();
  const createGroup = useCreateGroup();

  // Fetch all items for selected card (for View All dialog)
  const { data: cardItemsData } = useItems(
    selectedCardId && viewAllOpen
      ? { card_id: selectedCardId, limit: 100 }
      : undefined,
  );

  // Fetch full card detail (includes tags, recent_items, recent_logs)
  const { data: cardDetail } = useSceneCard(selectedCardId ?? '');

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleScene = (id: string) => {
    setExpandedScenes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Find basic card from tree (for tree display scores)
  const selectedCard: SceneCard | null = selectedCardId
    ? ((tree ?? [])
        .flatMap((g) => g.scenes.flatMap((s) => s.cards))
        .find((c) => c.id === selectedCardId) ?? null)
    : null;

  const tagMap = Object.fromEntries((tags ?? []).map((t) => [t.id, t]));

  // Use detail card when available, fall back to tree card
  const detail = cardDetail ?? selectedCard;

  // Detail extensions (may not exist on SceneCard type from tree)
  const detailAny = cardDetail as
    | (SceneCard & {
        tags?: Array<{ id: string; name: string; color: string | null; weight: number }>;
        recent_items?: Array<{
          id: string;
          text: string;
          item_type: string;
          status: string;
        }>;
        recent_logs?: Array<{
          id: string;
          date: string;
          log_type: string;
          score: number | null;
          duration_min: number | null;
        }>;
      })
    | null;

  /* ---------- loading skeleton ---------- */
  if (isLoading) {
    return (
      <div className="flex gap-4 h-[calc(100vh-8rem)]">
        <div className="w-72 space-y-2 p-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-7 bg-muted rounded animate-pulse" />
          ))}
        </div>
        <div className="flex-1 h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  const groups = tree ?? [];

  /* ---------- count helpers ---------- */
  const groupCardCount = (g: (typeof groups)[number]) =>
    g.scenes.reduce((sum, s) => sum + s.cards.length, 0);

  return (
    <div className="flex gap-4 h-[calc(100vh-8rem)]">
      {/* ============ LEFT PANEL — HIERARCHY ============ */}
      <div className="w-72 flex-shrink-0 border rounded-lg overflow-y-auto bg-card">
        {/* Panel header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b bg-muted/30">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">
            Hierarchy
          </span>
          <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowAddGroup(true)}>
            <Plus className="w-4 h-4" />
          </button>
          <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader><DialogTitle>Add Scene Group</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Input placeholder="Group name" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddGroup(false)}>Cancel</Button>
                <Button onClick={() => {
                  if (newGroupName.trim()) {
                    createGroup.mutate({ name: newGroupName.trim() }, {
                      onSuccess: () => { setNewGroupName(''); setShowAddGroup(false); }
                    });
                  }
                }} disabled={!newGroupName.trim()}>Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 text-center">No scenes yet</p>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {groups.map((group) => (
              <div key={group.id}>
                {/* Group row */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 text-sm font-medium"
                >
                  {expandedGroups.has(group.id) ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  )}
                  <span className="mr-1">&#128193;</span>
                  <span className="truncate">{group.name}</span>
                  <Badge
                    variant="secondary"
                    className="ml-auto text-[10px] px-1.5 py-0 h-5 shrink-0"
                  >
                    {groupCardCount(group)}
                  </Badge>
                </button>

                {/* Scenes under group */}
                {expandedGroups.has(group.id) && (
                  <div className="ml-5 space-y-0.5">
                    {group.scenes.map((scene) => (
                      <div key={scene.id}>
                        {/* Scene row */}
                        <button
                          onClick={() => toggleScene(scene.id)}
                          className="w-full flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted/50 text-xs font-medium text-muted-foreground"
                        >
                          {expandedScenes.has(scene.id) ? (
                            <ChevronDown className="w-3 h-3 shrink-0" />
                          ) : (
                            <ChevronRight className="w-3 h-3 shrink-0" />
                          )}
                          <FileText className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{scene.name}</span>
                          <span className="ml-auto text-[10px] text-muted-foreground/60 shrink-0">
                            {scene.cards.length}
                          </span>
                        </button>

                        {/* Cards under scene */}
                        {expandedScenes.has(scene.id) && (
                          <div className="ml-5 space-y-0.5">
                            {scene.cards.map((card) => (
                              <button
                                key={card.id}
                                onClick={() => setSelectedCardId(card.id)}
                                className={cn(
                                  'w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition-colors',
                                  selectedCardId === card.id
                                    ? 'bg-indigo-100 text-indigo-700 font-medium dark:bg-indigo-950 dark:text-indigo-300'
                                    : 'hover:bg-muted/50 text-foreground',
                                )}
                              >
                                <span className="truncate">{card.name}</span>
                                <span className="flex items-center gap-1 shrink-0 ml-1">
                                  {card.avg_score != null && (
                                    <span className="text-emerald-600 font-medium">
                                      {displayScore(card.avg_score)}
                                    </span>
                                  )}
                                  <RefreshCw className="w-3 h-3 text-muted-foreground/50" />
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============ RIGHT PANEL — DETAIL ============ */}
      <div className="flex-1 overflow-y-auto">
        {!detail ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Map className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a scene card to view details</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* ---- Card Header ---- */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 dark:bg-indigo-950">
                    <Map className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>

                  {/* Title + stars + tags */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold truncate">{detail.name}</h2>
                      <button className="text-muted-foreground hover:text-foreground shrink-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <DifficultyStars difficulty={detail.difficulty} />
                    {detail.core_situation && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {detail.core_situation}
                      </p>
                    )}
                    <div className="flex gap-1.5 flex-wrap pt-1">
                      {(detailAny?.tags ?? (detail.tag_ids ?? []).map((id: string) => tagMap[id])).map((tag: any) => {
                        if (!tag) return null;
                        const t = 'weight' in tag ? tag : (tag as any);
                        return (
                          <Badge
                            key={t.id}
                            variant="secondary"
                            className={cn('text-xs', tagColorClass(t.color ?? null))}
                          >
                            {t.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ---- Info cards + Stats (2-col layout) ---- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: 3 info cards stacked */}
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Core Situation */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5 text-blue-500" />
                        Core Situation
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground">
                        {detail.core_situation ?? 'No situation described'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Common Traps */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        Common Traps
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground">
                        {detail.common_traps ?? 'No traps documented'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Embodied Tip */}
                  <Card className="bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                        <Lightbulb className="w-3.5 h-3.5" />
                        Embodied Tip
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm italic text-emerald-800 dark:text-emerald-300">
                        {detail.embodied_tip ? `"${detail.embodied_tip}"` : 'No tip available'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* ---- Related Items Table ---- */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold">Related Items</CardTitle>
                      <button
                        className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                        onClick={() => setViewAllOpen(true)}
                      >
                        View All
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(detailAny?.recent_items ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No related items yet
                      </p>
                    ) : (
                      <div className="border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Item Name
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Type
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Mastery
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detailAny?.recent_items ?? []).map((item) => {
                              const statusToMastery: Record<string, number> = { 'new': 0, 'learning': 25, 'recognized': 60, 'mastered': 100 };
                              const mastery = statusToMastery[item.status] ?? 0;
                              return (
                                <tr key={item.id} className="border-t">
                                  <td className="px-3 py-2 font-medium">{item.text}</td>
                                  <td className="px-3 py-2">
                                    <Badge variant="outline" className="text-xs">
                                      {item.item_type}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <Progress value={mastery} className="h-2 flex-1" />
                                      <span className="text-xs text-muted-foreground w-9 text-right">
                                        {mastery.toFixed(0)}%
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* ---- Recent Activity ---- */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(detailAny?.recent_logs ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No activity recorded
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {(detailAny?.recent_logs ?? []).map((log) => {
                          const logTypeColor = LOG_TYPE_COLORS[log.log_type] ?? 'bg-gray-100 text-gray-700';
                          return (
                            <button
                              key={log.id}
                              onClick={() => navigate(`/logs?id=${log.id}`)}
                              className="flex items-start gap-3 text-sm border-l-2 border-indigo-200 pl-3 dark:border-indigo-800 w-full text-left hover:bg-muted/50 rounded-r transition-colors"
                            >
                              <div className="flex-1">
                                <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium capitalize', logTypeColor)}>
                                  {log.log_type ?? 'training'}
                                </span>
                                <span className="text-muted-foreground ml-2">
                                  {formatRelativeTime(log.date)}
                                  {log.score != null && (
                                    <span className="ml-1 font-medium text-foreground">
                                      &bull; Score {displayScore(log.score)}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right column: Scene Stats + Action buttons */}
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Scene Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Practices</span>
                      <span className="font-semibold">{detail.practice_count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Avg Score</span>
                      <span className="font-semibold">{displayScore(detail.avg_score)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Latest Run</span>
                      <span className="font-semibold">{displayScore(detail.latest_score)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Last Session</span>
                      <span className="font-semibold text-xs">
                        {detail.last_practiced
                          ? formatRelativeTime(detail.last_practiced)
                          : 'Never'}
                      </span>
                    </div>

                    <div className="pt-2 space-y-2">
                      <div className="w-full">
                        <CopyButton
                          text={`/cadence train ${detail.sid ?? detail.id}`}
                          label="Copy Training Cmd"
                        />
                      </div>
                      <Button
                        className="w-full bg-rose-500 hover:bg-rose-600 text-white"
                        onClick={() => {
                          const cardRef = detail.sid ?? detail.id;
                          navigator.clipboard.writeText(
                            `/cadence warmup ${cardRef}`,
                          );
                          toast({
                            title: 'Command Copied',
                            description: `Paste /cadence warmup ${cardRef} in your AI chat to start a warmup session.`,
                          });
                        }}
                      >
                        <Play className="w-4 h-4 mr-1.5" />
                        Start Warmup
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View All Items Dialog */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Items — {detail?.name ?? 'Card'}</DialogTitle>
          </DialogHeader>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Item</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Level</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">CEFR</th>
                </tr>
              </thead>
              <tbody>
                {(cardItemsData?.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                      No items associated with this card
                    </td>
                  </tr>
                ) : (
                  (cardItemsData?.data ?? []).map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-3 py-2">
                        <div className="font-medium">{item.text}</div>
                        {item.definition && (
                          <div className="text-xs text-muted-foreground truncate max-w-[250px]">{item.definition}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">{item.item_type}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-amber-500">{'★'.repeat(Math.min(item.level, 3))}</span>
                        <span className="text-muted-foreground/30">{'★'.repeat(Math.max(0, 3 - item.level))}</span>
                      </td>
                      <td className="px-3 py-2 text-xs capitalize">{item.status}</td>
                      <td className="px-3 py-2 text-xs">{item.cefr_level ?? '--'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
